import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PrimitiveAtom } from 'jotai';

import type {
  ProjectCompaniesSort,
  ProjectListing,
} from '@/api/v4/projects.api';
import { FilterSearchInput } from '@/components/filters/filter-search-input';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { InfiniteScrollTable } from '@/ui/table/infinite-table/infinite-scroll-table';
import type { IHeaderRow, ISort, Row } from '@/ui/table/table.types';
import type { FetchNextPageOptions } from '@tanstack/react-query';

import { TABLE_CONTAINER_HEIGHT } from '../constants/enhance.constants';
import { Subheader } from '../styles/enhance-modal.styles';

interface CompanySelectionViewProps {
  dataTestId?: string;
  fetchNextPage?: (options?: FetchNextPageOptions) => Promise<unknown>;
  hasNextPage?: boolean;
  headers: IHeaderRow<ProjectCompaniesSort>;
  isFetchingNextPage?: boolean;
  onSearchChange?: (value: string) => void;
  projectListings: ProjectListing[];
  rows: Row[];
  searchValue?: string;
  sortState: PrimitiveAtom<ISort<ProjectCompaniesSort>>;
  tableHeight?: string;
}

export const CompanySelectionView = ({
  dataTestId = 'company-selection-view',
  fetchNextPage,
  hasNextPage = false,
  headers,
  isFetchingNextPage = false,
  onSearchChange,
  projectListings,
  rows,
  searchValue = '',
  sortState,
  tableHeight = TABLE_CONTAINER_HEIGHT,
}: CompanySelectionViewProps) => {
  const { t } = useTranslation('projects');
  const [isSearchInputActive, setIsSearchInputActive] = useState(false);

  const handleRemoveSearchInput = () => {
    onSearchChange?.('');
  };

  const handleDeactivateSearchInput = () => {
    if (!searchValue) {
      setIsSearchInputActive(false);
    }
  };

  return (
    <>
      <Subheader>{t`enhanceConfigCompaniesSubheader1`}</Subheader>
      <Subheader>{t`enhanceConfigCompaniesSubheader2`}</Subheader>
      <Flexbox padding="10px 22px" justify="flex-end">
        <FilterSearchInput
          onChange={(value: string) => onSearchChange?.(value)}
          placeholder={t`enhanceConfigSearchPlaceholder`}
          onRemove={handleRemoveSearchInput}
          isInputActivated={isSearchInputActive || !!searchValue}
          onInputActivate={() => setIsSearchInputActive(true)}
          onInputDeactivate={handleDeactivateSearchInput}
          defaultValue={searchValue}
          delay={300}
        />
      </Flexbox>
      <Flexbox
        data-testid={dataTestId}
        direction="column"
        gap="8px"
        name="company-selection-body"
      >
        <InfiniteScrollTable<ProjectListing, ProjectCompaniesSort>
          id="company-selection-table"
          data={projectListings}
          hasMore={hasNextPage}
          headers={headers}
          height={tableHeight}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={false}
          onLoadMore={fetchNextPage}
          rows={rows}
          sortState={sortState}
          tableLayout="fixed"
        />
      </Flexbox>
    </>
  );
};
